import React, { useEffect, useState } from "react";
import Modal from "../Modal/Modal";
import { FileText, ZoomIn, ZoomOut, RotateCcw } from "lucide-react";

interface PDFViewerModalProps {
  isOpen: boolean;
  onClose: () => void;
  pdfUrl: string;
  title?: string;
  onRegister?: (bringToFront: () => void) => void; 

}

const PDFViewerModal: React.FC<PDFViewerModalProps> = ({
  isOpen,
  onClose,
  pdfUrl,
  title = "PDF Viewer",
  onRegister
}) => {
  const [zoom, setZoom] = useState(100);

  // Reset zoom when modal opens or url changes
  useEffect(() => {
    if (isOpen) {
      setZoom(100);
    }
  }, [isOpen, pdfUrl]);

  // Disable right-click context menu and keyboard shortcuts
  useEffect(() => {
    const handleContextMenu = (e: MouseEvent) => {
      e.preventDefault();
      return false;
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (
        (e.ctrlKey || e.metaKey) &&
        (e.key === "s" ||
          e.key === "S" ||
          e.key === "p" ||
          e.key === "P" ||
          e.key === "u" ||
          e.key === "U")
      ) {
        e.preventDefault();
        return false;
      }
      if (
        e.key === "F12" ||
        ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === "I")
      ) {
        e.preventDefault();
        return false;
      }
    };

    if (isOpen) {
      document.addEventListener("contextmenu", handleContextMenu, true);
      document.addEventListener("keydown", handleKeyDown, true);
    }

    return () => {
      document.removeEventListener("contextmenu", handleContextMenu, true);
      document.removeEventListener("keydown", handleKeyDown, true);
    };
  }, [isOpen]);

  // Construct PDF URL with parameters to hide toolbar/download options
  const getPDFUrl = () => {
    if (!pdfUrl) return "";

    // Ensure URL is absolute or root-relative
    let cleanUrl = pdfUrl;
    if (!cleanUrl.startsWith("http") && !cleanUrl.startsWith("/")) {
      // If it's a relative path, assume it's from the API root
      const baseUrl = import.meta.env.VITE_AAA_API_URL || "";
      cleanUrl = `${baseUrl.replace(/\/$/, "")}/${cleanUrl}`;
    }

    const params = `toolbar=0&navpanes=0&scrollbar=1&zoom=${zoom}`;

    // If URL already has a fragment, append with &; otherwise add with #
    return cleanUrl.includes("#")
      ? `${cleanUrl}&${params}`
      : `${cleanUrl}#${params}`;
  };

  const handleZoomIn = () => {
    setZoom((prev) => Math.min(prev + 10, 200));
  };

  const handleZoomOut = () => {
    setZoom((prev) => Math.max(prev - 10, 50));
  };

  const handleResetZoom = () => {
    setZoom(100);
  };

  const headerContent = (
    <div className="flex items-center justify-between w-full">
      <div className="flex items-center gap-2">
        <FileText size={18} />
        <span>{title}</span>
      </div>
      <div className="flex items-center gap-2 mr-8">
        <button
          onClick={handleZoomOut}
          className="p-1 hover:bg-gray-200 rounded"
          title="Zoom Out"
        >
          <ZoomOut size={18} />
        </button>
        <span className="text-sm w-12 text-center">{zoom}%</span>
        <button
          onClick={handleZoomIn}
          className="p-1 hover:bg-gray-200 rounded"
          title="Zoom In"
        >
          <ZoomIn size={18} />
        </button>
        <button
          onClick={handleResetZoom}
          className="p-1 hover:bg-gray-200 rounded ml-2"
          title="Reset Zoom"
        >
          <RotateCcw size={18} />
        </button>
      </div>
    </div>
  );

  return (
    <Modal
      header={headerContent}
      isOpen={isOpen}
      closeModal={onClose}
      resizable={true}
      onRegister={onRegister}
    >
      <div
        className="w-full h-full relative select-none overflow-hidden bg-gray-100"
        style={{
          minWidth: "60svw",
          minHeight: "50svh",
        }}
        onContextMenu={(e) => {
          e.preventDefault();
          return false;
        }}
      >
        {pdfUrl ? (
          <iframe
            key={`${pdfUrl}-${zoom}`}
            src={getPDFUrl()}
            className="w-full h-full border-none"
            title={title}
            style={{
              width: "100%",
              height: "100%",
              userSelect: "none",
            }}
          />
        ) : (
          <div className="flex items-center justify-center h-full">
            <p className="text-gray-500">No PDF URL provided</p>
          </div>
        )}

        {/* Transparent overlay to catch events and prevent selection/right-click on the viewer UI */}
        <div
          className="absolute top-0 left-0 w-full h-12 pointer-events-auto bg-transparent z-10"
          onContextMenu={(e) => e.preventDefault()}
        />
      </div>
    </Modal>
  );
};

export default PDFViewerModal;
